/* ---------------------------------------------------------------------------
 * PRODUCT ADD-ON
 *
 * Drives the add-on card rendered by snippets/product-addon.liquid on the
 * sunglasses PDPs. The Liquid emits every selectable variant as one flat JSON
 * array, so this file never has to care whether colors are separate products
 * (the Heritage Tee line) or variants of one product (the KIAURA 500 hat).
 *
 * Everything is already in the DOM on load. This only ever changes state:
 * which swatch is current, which sizes are selectable, what the price reads,
 * and which variant id the button posts. Nothing is built here, so the card
 * cannot pop or reflow.
 *
 * Delegated listeners rather than per-element binding, so the card survives
 * being re-rendered by the Section Rendering API.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  var money = function (cents) {
    /* Symmetry exposes the shop's format; fall back to plain dollars. */
    var fmt = (window.theme && theme.settings && theme.settings.moneyWithCurrencyFormat) || '';
    var val = (cents / 100).toFixed(2).replace(/\.00$/, '');
    if (/\{\{\s*amount/.test(fmt)) return '$' + val;
    return '$' + val;
  };

  function parse(root) {
    var tag = root.querySelector('[data-addon-variants]');
    if (!tag) return [];
    try {
      return JSON.parse(tag.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  function state(root) {
    if (!root.__addon) {
      var rows = parse(root);
      root.__addon = {
        rows: rows,
        /* Start on the first color that has anything in stock, so the card
           never opens on a dead colorway. */
        color: (rows.filter(function (r) { return r.available; })[0] || rows[0] || {}).colorKey || null,
        discount: parseFloat(root.getAttribute('data-discount')) || 0
      };
    }
    return root.__addon;
  }

  function selectedSize(root) {
    var checked = root.querySelector('.product-addon__sizes input:checked');
    return checked ? checked.value : null;
  }

  /* The one row matching the current color + size, or null. */
  function current(root) {
    var s = state(root);
    var size = selectedSize(root);
    var pool = s.rows.filter(function (r) { return r.colorKey === s.color; });
    if (!pool.length) return null;
    if (!size) return pool[0];
    return pool.filter(function (r) { return r.size === size; })[0] || null;
  }

  function render(root) {
    var s = state(root);
    var pool = s.rows.filter(function (r) { return r.colorKey === s.color; });

    /* Swatches */
    root.querySelectorAll('[data-tee-pick]').forEach(function (b) {
      b.classList.toggle('is-current', b.getAttribute('data-tee-pick') === s.color);
    });

    var label = root.querySelector('[data-addon-colorlabel]');
    if (label && pool.length) label.textContent = pool[0].color || '';

    /* Image */
    var img = root.querySelector('[data-addon-image]');
    if (img && pool.length && pool[0].image) img.src = pool[0].image;

    /* Sizes: disable what this color cannot fill, rather than removing it.
       Removing would shrink the row and shift everything below it. */
    var sizes = root.querySelectorAll('.product-addon__sizes input');
    var checkedStillValid = false;
    sizes.forEach(function (input) {
      var row = pool.filter(function (r) { return r.size === input.value; })[0];
      var ok = !!(row && row.available);
      input.disabled = !ok;
      input.closest('.product-addon__size').classList.toggle('is-disabled', !ok);
      if (input.checked && ok) checkedStillValid = true;
    });

    /* The previously chosen size is gone in this color, so move to the first
       one that works instead of leaving an impossible selection standing. */
    if (sizes.length && !checkedStillValid) {
      var first = Array.prototype.filter.call(sizes, function (i) { return !i.disabled; })[0];
      if (first) first.checked = true;
    }

    var row = current(root);
    var priceEl = root.querySelector('[data-addon-price]');
    var wasEl = root.querySelector('[data-addon-was]');
    var btn = root.querySelector('[data-addon-add]');

    /* Price and strike both come from the variant itself. The card used to
       compute a discounted price from a theme setting, which could advertise a
       number checkout would not honour. Reading compare-at means the two are
       physically the same value. */
    if (priceEl && row) {
      priceEl.textContent = money(row.price);
      if (wasEl) wasEl.textContent = row.compareAt > row.price ? money(row.compareAt) : '';
    }

    if (btn) {
      var usable = !!(row && row.available);
      btn.disabled = !usable;
      btn.classList.toggle('is-disabled', !usable);
      if (!usable) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    }
  }

  function fail(root, msg) {
    var el = root.querySelector('[data-addon-error]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    setTimeout(function () { el.hidden = true; }, 6000);
  }

  /* These variants are priced as an add-on, so they must never be the only
     thing in the cart. Checking at click time rather than mutating the cart
     afterwards: nothing to race against UpCart, and the shopper is told why
     instead of watching an item disappear.

     Detection is by variant id from our own payload, not by handle matching,
     so it stays correct if the add-on products are ever renamed. */
  function cartHasSomethingElse(root) {
    var ids = {};
    state(root).rows.forEach(function (r) { ids[r.id] = true; });
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return (cart.items || []).some(function (i) { return !ids[i.variant_id]; });
      })
      .catch(function () { return true; });   // never block on a failed check
  }

  function add(root) {
    var row = current(root);
    if (!row) return fail(root, 'Pick a color and size first.');

    var btn = root.querySelector('[data-addon-add]');
    var restore = btn ? btn.textContent : null;
    if (btn) { btn.disabled = true; btn.textContent = 'Checking...'; }

    cartHasSomethingElse(root).then(function (ok) {
      if (!ok) {
        if (btn) { btn.disabled = false; btn.textContent = restore; }
        return fail(root, root.getAttribute('data-gate-message') ||
          'This price is only available as an add-on. Add a frame to your cart first.');
      }
      doAdd(root, row, btn, restore);
    });
  }

  function doAdd(root, row, btn, restore) {
    if (btn) { btn.textContent = 'Adding...'; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: row.id, quantity: 1 }] })
    })
      .then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      })
      .catch(function () { fail(root, 'Could not add that. Try again.'); })
      .finally(function () {
        if (btn) { btn.textContent = restore; }
        render(root);
      });
  }

  document.addEventListener('click', function (e) {
    var sw = e.target.closest ? e.target.closest('.product-addon [data-tee-pick]') : null;
    if (sw) {
      e.preventDefault();
      var root = sw.closest('[data-product-addon]');
      state(root).color = sw.getAttribute('data-tee-pick');
      render(root);
      return;
    }
    var btn = e.target.closest ? e.target.closest('[data-addon-add]') : null;
    if (btn) {
      e.preventDefault();
      add(btn.closest('[data-product-addon]'));
    }
  });

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('.product-addon__sizes input')) return;
    render(t.closest('[data-product-addon]'));
  });

  function boot() {
    document.querySelectorAll('[data-product-addon]').forEach(render);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Section Rendering API swaps replace the node, so re-run on Shopify's
     section load event as well as first paint. */
  document.addEventListener('shopify:section:load', boot);
})();
